import React, { useEffect, useMemo, useRef, useState } from "react";
import { Tiptap, useEditor } from "@tiptap/react";
import { formatMergeTag } from "../../domain/merge-tags";
import { EmailBuilderMergeTagAutocomplete } from "../EmailBuilderMergeTagAutocomplete";
import {
  buildEmailBuilderMergeTagSuggestions,
  findEmailBuilderMergeTagToken,
  measureEmailBuilderMergeTagAutocompleteSafeBounds,
  moveEmailBuilderMergeTagActiveIndex,
  resolveEmailBuilderMergeTagAutocompleteAnchor,
  resolveEmailBuilderMergeTagContentBounds,
  type IEmailBuilderMergeTagAutocompletePosition,
  type IEmailBuilderMergeTagSuggestion,
  type IEmailBuilderMergeTagToken,
  type TEmailBuilderMergeTag,
} from "../emailBuilderMergeTagMatching";
import {
  emailBuilderHeadingRichTextExtensions,
  emailBuilderLabelRichTextExtensions,
  emailBuilderParagraphRichTextExtensions,
  type TEmailBuilderHeadingLevel,
} from "./emailBuilderRichTextExtensions";
import {
  EMAIL_BUILDER_LABEL_EDITOR_FONT_WEIGHT,
  emailBuilderLabelEditorContent,
  emailBuilderRichTextEditorHtml,
  emailBuilderRichTextStoredHtml,
  type TEmailBuilderRichTextKind,
} from "./emailBuilderRichTextHtml";
import {
  readEmailBuilderRichTextCaretContext,
  registerEmailBuilderRichTextSession,
  takeEmailBuilderRichTextCaretPoint,
  unregisterEmailBuilderRichTextSession,
} from "./emailBuilderRichTextSession";

export interface IEmailBuilderRichTextEditorProps {
  blockId: string;
  kind: TEmailBuilderRichTextKind;
  headingLevel?: TEmailBuilderHeadingLevel;
  content: string;
  editorClassName: string;
  editorStyle?: React.CSSProperties;
  ariaLabel: string;
  placeholder: string;
  fontWeight?: number;
  mergeTags?: readonly TEmailBuilderMergeTag[];
  contentAttribute: "data-email-builder-inline-text" | "data-email-builder-inline-button-label";
}

interface IMergeTagUi {
  open: boolean;
  token: IEmailBuilderMergeTagToken | null;
  suggestions: readonly IEmailBuilderMergeTagSuggestion[];
  activeIndex: number;
}

export function EmailBuilderRichTextEditor(props: IEmailBuilderRichTextEditorProps) {
  const {
    blockId,
    kind,
    headingLevel,
    content,
    editorClassName,
    editorStyle,
    ariaLabel,
    placeholder,
    fontWeight,
    mergeTags,
    contentAttribute,
  } = props;
  const mergeTagsRef = useRef(mergeTags);
  const dismissedTokenRef = useRef<string | null>(null);
  const mergeTagUiRef = useRef<IMergeTagUi>({
    open: false,
    token: null,
    suggestions: [],
    activeIndex: 0,
  });
  const [mergeTagUi, setMergeTagUi] = useState<IMergeTagUi>(mergeTagUiRef.current);
  const [mergeTagPosition, setMergeTagPosition] = useState<IEmailBuilderMergeTagAutocompletePosition | null>(null);
  const insertSuggestionRef = useRef<(tag: TEmailBuilderMergeTag) => void>(() => undefined);
  const sourceHtmlRef = useRef(
    kind === "label" ? emailBuilderLabelEditorContent(content, fontWeight) : content,
  );
  mergeTagsRef.current = mergeTags;

  const extensions = useMemo(() => {
    if (kind === "heading") {
      return emailBuilderHeadingRichTextExtensions(headingLevel ?? 1, placeholder);
    }

    if (kind === "label") {
      return emailBuilderLabelRichTextExtensions(placeholder);
    }

    return emailBuilderParagraphRichTextExtensions(placeholder);
  }, [headingLevel, kind, placeholder]);

  const editor = useEditor({
    extensions,
    content: emailBuilderRichTextEditorHtml({
      kind,
      html: sourceHtmlRef.current,
      headingLevel,
    }),
    autofocus: "end",
    editorProps: {
      attributes: {
        class: editorClassName,
        role: "textbox",
        "aria-multiline": "true",
        "aria-label": plainAriaLabel(ariaLabel),
        [contentAttribute]: blockId,
      },
      handleKeyDown: (_view, event) => {
        const ui = mergeTagUiRef.current;
        if (!ui.open) {
          return false;
        }

        if (event.key === "ArrowDown") {
          event.preventDefault();
          updateMergeTagUi(current => ({
            ...current,
            activeIndex: moveEmailBuilderMergeTagActiveIndex(current.activeIndex, 1, current.suggestions.length),
          }));
          return true;
        }

        if (event.key === "ArrowUp") {
          event.preventDefault();
          updateMergeTagUi(current => ({
            ...current,
            activeIndex: moveEmailBuilderMergeTagActiveIndex(current.activeIndex, -1, current.suggestions.length),
          }));
          return true;
        }

        if (event.key === "Enter" || event.key === "Tab") {
          event.preventDefault();
          const suggestion = ui.suggestions[ui.activeIndex];
          if (suggestion !== undefined) {
            insertSuggestionRef.current(suggestion.tag);
          }
          return true;
        }

        if (event.key === "Escape") {
          event.preventDefault();
          dismissedTokenRef.current = ui.token?.tokenText ?? null;
          closeMergeTags();
          return true;
        }

        return false;
      },
    },
    onCreate: ({ editor: created }) => {
      sourceHtmlRef.current = created.getHTML();
      const point = takeEmailBuilderRichTextCaretPoint(blockId);
      if (point === null) {
        return;
      }

      const position = created.view.posAtCoords({ left: point.x, top: point.y });
      if (position !== null) {
        created.commands.setTextSelection(position.pos);
      }
    },
    onUpdate: ({ editor: current }) => {
      sourceHtmlRef.current = current.getHTML();
    },
  }, [kind, headingLevel]);

  function updateMergeTagUi(next: IMergeTagUi | ((current: IMergeTagUi) => IMergeTagUi)): void {
    const resolved = typeof next === "function" ? next(mergeTagUiRef.current) : next;
    mergeTagUiRef.current = resolved;
    setMergeTagUi(resolved);
  }

  function closeMergeTags(): void {
    updateMergeTagUi({ open: false, token: null, suggestions: [], activeIndex: 0 });
    setMergeTagPosition(null);
  }

  function insertSuggestion(tag: TEmailBuilderMergeTag): void {
    if (editor === null) {
      return;
    }

    const context = readEmailBuilderRichTextCaretContext(editor);
    const token = context === null ? null : findEmailBuilderMergeTagToken(context.textBefore, context.textAfter);
    if (context === null || token === null) {
      closeMergeTags();
      return;
    }

    const from = context.pos - (context.textBefore.length - token.start);
    const to = context.pos + (token.end - context.textBefore.length);
    editor.chain().focus().insertContentAt({ from, to }, formatMergeTag(tag)).run();
    dismissedTokenRef.current = null;
    closeMergeTags();
  }

  insertSuggestionRef.current = insertSuggestion;

  useEffect(() => {
    if (editor === null || editor.isDestroyed) {
      return;
    }

    registerEmailBuilderRichTextSession({
      blockId,
      editor,
      readStoredHtml: () => emailBuilderRichTextStoredHtml(kind, editor.getHTML()),
    });

    return () => {
      unregisterEmailBuilderRichTextSession(blockId);
    };
  }, [blockId, editor, kind]);

  useEffect(() => {
    if (editor === null || mergeTags === undefined || mergeTags.length === 0) {
      return;
    }

    function syncMergeTags(): void {
      if (editor === null) {
        return;
      }

      const context = readEmailBuilderRichTextCaretContext(editor);
      const token = context === null ? null : findEmailBuilderMergeTagToken(context.textBefore, context.textAfter);
      if (token === null || token.openingLength < 2) {
        if (mergeTagUiRef.current.open) {
          closeMergeTags();
        }
        return;
      }

      if (dismissedTokenRef.current === token.tokenText) {
        return;
      }

      dismissedTokenRef.current = null;
      const suggestions = buildEmailBuilderMergeTagSuggestions(token, mergeTagsRef.current);
      if (suggestions.length === 0) {
        closeMergeTags();
        return;
      }

      updateMergeTagUi(current => ({
        open: true,
        token,
        suggestions,
        activeIndex:
          current.token?.tokenText === token.tokenText ? Math.min(current.activeIndex, suggestions.length - 1) : 0,
      }));

      const coords = editor.view.coordsAtPos(editor.state.selection.from);
      const editorElement = editor.view.dom;
      setMergeTagPosition(
        resolveEmailBuilderMergeTagAutocompleteAnchor({
          caretRect: {
            left: coords.left,
            top: coords.top,
            right: coords.right,
            bottom: coords.bottom,
            width: coords.right - coords.left,
            height: coords.bottom - coords.top,
          },
          fallbackRect: editorElement.getBoundingClientRect(),
          safeBounds: measureEmailBuilderMergeTagAutocompleteSafeBounds(),
          contentBounds: resolveEmailBuilderMergeTagContentBounds(editorElement),
        }),
      );
    }

    editor.on("selectionUpdate", syncMergeTags);
    editor.on("update", syncMergeTags);
    return () => {
      editor.off("selectionUpdate", syncMergeTags);
      editor.off("update", syncMergeTags);
    };
  }, [editor, mergeTags]);

  if (editor === null) {
    return null;
  }

  const listboxId = `email-builder-merge-tag-listbox-${blockId}`;

  return (
    <>
      <Tiptap editor={editor}>
        <Tiptap.Content
          style={
            kind === "label"
              ? { ...editorStyle, fontWeight: EMAIL_BUILDER_LABEL_EDITOR_FONT_WEIGHT }
              : editorStyle
          }
        />
      </Tiptap>
      {mergeTagUi.open && mergeTagPosition !== null ? (
        <EmailBuilderMergeTagAutocomplete
          suggestions={mergeTagUi.suggestions}
          activeIndex={mergeTagUi.activeIndex}
          position={mergeTagPosition}
          listboxId={listboxId}
          onActiveIndexChange={index => {
            updateMergeTagUi(current => ({ ...current, activeIndex: index }));
          }}
          onSelect={insertSuggestion}
        />
      ) : null}
    </>
  );
}

function plainAriaLabel(value: string): string {
  const plain = value.replace(/<[^>]+>/g, "").replace(/&nbsp;/gi, " ").trim();
  return plain.length > 0 ? plain : value;
}

export function emailBuilderRichTextHeadingLevel(variant: string): TEmailBuilderHeadingLevel {
  const level = Number(variant.replace("h", ""));
  if (level === 1 || level === 2 || level === 3 || level === 4 || level === 5 || level === 6) {
    return level;
  }

  return 1;
}
